import React, { useRef, useState } from 'react';
import {
  Activity,
  AudioWaveform as Waveform,
  BookOpen,
  Brain,
  Download,
  Upload,
  Zap,
} from 'lucide-react';
import {
  downloadPortableBundle,
  getLocalDataSummary,
  getRecentWorkflowDraft,
  importPortableBundle,
  type ImportMode,
} from '../services/offlineDataPortabilityService';

interface HomePageProps {
  onStartEvaluation: () => void;
  onOpenAtlas: () => void;
  onContinueDraft: (draftId: string) => void;
}

const HomePage: React.FC<HomePageProps> = ({
  onStartEvaluation,
  onOpenAtlas,
  onContinueDraft,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const recentDraft = getRecentWorkflowDraft();
  const localSummary = getLocalDataSummary();

  const handleImport = async (file: File, mode: ImportMode) => {
    setImportMessage(null);
    setImportError(null);
    try {
      const text = await file.text();
      const result = await importPortableBundle(text, mode);
      setImportMessage(
        `Importación completada: ${result.patientsImported} pacientes, ${result.studiesImported} estudios, ${result.draftsImported} borradores.`,
      );
    } catch (error) {
      setImportError(error instanceof Error ? error.message : 'No se pudo importar el backup');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-purple-900/20" />
        <div className="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-gray-900 to-transparent" />
        <div className="absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-gray-900 to-transparent" />
      </div>

      <div className="relative z-10">
        <nav className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Brain className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" />
              <span className="text-lg sm:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
                DLM EMG
              </span>
            </div>
          </div>
        </nav>

        <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center mb-6 sm:mb-8">
              <Activity className="h-8 w-8 sm:h-10 sm:w-10 text-blue-400 mr-3 sm:mr-4" />
              <Waveform className="h-8 w-8 sm:h-10 sm:w-10 text-purple-400" />
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400 px-4">
              Electromiografía DLM
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-6 sm:mb-8 leading-relaxed px-4">
              Revolucionando el diagnóstico neurofisiológico con tecnología avanzada e inteligencia artificial
            </p>

            <p className="text-sm sm:text-base md:text-lg text-gray-400 mb-8 sm:mb-12 max-w-2xl mx-auto px-4">
              Combine la precisión del análisis electromiográfico con herramientas de enseñanza interactivas para un diagnóstico más preciso y una experiencia de aprendizaje enriquecedora
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
              <button
                onClick={onStartEvaluation}
                className="group relative inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-bold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-full overflow-hidden transition-all duration-300 hover:from-blue-500 hover:to-purple-500 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 transform hover:scale-105 w-full sm:w-auto"
              >
                <Zap className="w-5 h-5 sm:w-6 sm:h-6 mr-2 animate-pulse" />
                Iniciar Evaluación
                <div className="absolute inset-0 bg-white/20 transform translate-y-12 group-hover:translate-y-0 transition-transform duration-300" />
              </button>

              <button
                onClick={onOpenAtlas}
                className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold text-white border border-purple-500/60 rounded-full hover:bg-purple-900/30 transition-all duration-300 w-full sm:w-auto"
              >
                <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                Abrir Atlas ENMG
              </button>
            </div>

            {recentDraft && (
              <div className="mt-8 mx-auto max-w-2xl rounded-xl border border-amber-500/40 bg-amber-900/20 p-4 text-left">
                <p className="text-sm text-amber-100 font-medium mb-2">Evaluación en curso detectada</p>
                <p className="text-xs text-amber-200/80 mb-3">
                  Paciente: {recentDraft.patient.firstName} {recentDraft.patient.lastName || recentDraft.patient.id}
                  {' · '}
                  Paso: {recentDraft.currentStep}
                </p>
                <button
                  onClick={() => onContinueDraft(recentDraft.id)}
                  className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-500"
                >
                  Continuar evaluación
                </button>
              </div>
            )}
          </div>

          <div className="mt-12 sm:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-gray-800/50 backdrop-blur-sm p-4 sm:p-6 rounded-xl border border-gray-700 transform hover:scale-105 transition-transform duration-300">
              <Activity className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400 mb-3 sm:mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">Análisis Preciso</h3>
              <p className="text-sm sm:text-base text-gray-400">Evaluación detallada de la actividad muscular y nerviosa</p>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm p-4 sm:p-6 rounded-xl border border-gray-700 transform hover:scale-105 transition-transform duration-300">
              <Brain className="h-6 w-6 sm:h-8 sm:w-8 text-purple-400 mb-3 sm:mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">Asistida por bibliografía clínica</h3>
              <p className="text-sm sm:text-base text-gray-400">Interpretación avanzada de resultados</p>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm p-4 sm:p-6 rounded-xl border border-gray-700 transform hover:scale-105 transition-transform duration-300">
              <Waveform className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400 mb-3 sm:mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">Visualización Dinámica</h3>
              <p className="text-sm sm:text-base text-gray-400">Representación visual clara y detallada de los patrones electromiográficos</p>
            </div>
          </div>

          <section className="mt-12 max-w-3xl mx-auto rounded-xl border border-gray-700 bg-gray-800/40 p-6">
            <h2 className="text-xl font-semibold mb-2">Datos locales</h2>
            <p className="text-sm text-gray-400 mb-4">
              {localSummary.patientCount} pacientes · {localSummary.studyCount} estudios · {localSummary.draftCount} borradores guardados
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => downloadPortableBundle()}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500"
              >
                <Download className="h-4 w-4" />
                Exportar backup
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-600 px-4 py-2 text-sm font-medium text-gray-200 hover:bg-gray-700"
              >
                <Upload className="h-4 w-4" />
                Importar backup
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                const mode = window.confirm('¿Fusionar con datos existentes? Cancelar = reemplazar todo.')
                  ? 'merge'
                  : 'replace';
                void handleImport(file, mode);
                event.target.value = '';
              }}
            />
            {importMessage && <p className="mt-3 text-sm text-green-300">{importMessage}</p>}
            {importError && <p className="mt-3 text-sm text-red-300">{importError}</p>}
          </section>
        </main>
      </div>
    </div>
  );
};

export default HomePage;
